import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import WebHttpClient from '~src/httpClient/WebHttpClient';

import { CreateContentRelayPlanRequest } from '../serverApi/requests/CreateContentRelayPlanRequest';
import { DeleteContentRelayPlanRequest } from '../serverApi/requests/DeleteContentRelayPlanRequest';
import { GetContentRelayPlanRequest } from '../serverApi/requests/GetContentRelayPlanRequest';
import { UpdateContentRelayPlanRequest } from '../serverApi/requests/UpdateContentRelayPlanRequest';
import { IContentRelayPlanDetail, ISelectionQuery } from '../serverApi/types/ISelectionQuery';
import { mergeSelectionQueries } from './relaySelection';

/** Field mask (snake_case) sent to `updatePlan` to change only the membership. */
const SELECTION_QUERIES_MASK = ['selection_queries'];

/**
 * Reads the plan an update is about to rewrite. Every writer below resends fields from this read
 * (name, description, selection), and the service applies `selection_queries` as a wholesale replace —
 * an empty list clears the plan (tabhf-content-mobility DefaultPlanRecordMapper.toUpdatedColumns). So
 * a getPlan that resolves HTTP 200 with an `errors` payload, or with no plan, must fail the write
 * rather than let it send an empty selection / blank name built from the missing plan.
 */
function readPlanForWrite(luid: string): Promise<IContentRelayPlanDetail> {
  return WebHttpClient.sendRequest(new GetContentRelayPlanRequest({ luid: luid }))
    .then(function(plan: any) {
      if (!plan || hasErrors(plan) || !plan.luid) {
        return Promise.reject(plan);
      }
      return plan as IContentRelayPlanDetail;
    });
}

function hasErrors(result: any): boolean {
  return !!(result && result.errors && result.errors.length);
}

/** Creates a new Content Relay plan whose membership is the given selection. */
export function createRelayPlanWithSelection(
  name: string,
  selectionQueries: ISelectionQuery[]
): Promise<IContentRelayPlanDetail> {
  return WebHttpClient.sendRequest(new CreateContentRelayPlanRequest({
    name: name,
    description: '',
    selectionQueries: selectionQueries,
  }));
}

/**
 * Adds the given selection to an existing plan and resolves with how many items were newly added.
 * Reads the plan's current selection first because `updatePlan` replaces `selectionQueries`
 * wholesale, so the merged set must be sent.
 */
export function addSelectionToRelayPlan(
  luid: string,
  incoming: ISelectionQuery[]
): Promise<number> {
  return readPlanForWrite(luid)
    .then(function(plan) {
      const result = mergeSelectionQueries(plan.selectionQueries || [], incoming);
      // The mask is honored end to end: the monolith's WebClientContentMobilityAppService.updatePlan
      // forwards updateMask unchanged to the gRPC client, and tabhf-content-mobility
      // DefaultPlanRecordMapper.toUpdatedColumns writes only masked columns. The current
      // name/description are still resent because an empty mask means full replace, and the service
      // rejects a blank name ("name cannot be cleared").
      return WebHttpClient.sendRequest(new UpdateContentRelayPlanRequest({
        luid: luid,
        name: plan.name,
        description: plan.description,
        selectionQueries: result.merged,
        updateMask: SELECTION_QUERIES_MASK,
      })).then(function(updated: any) { return hasErrors(updated) ? Promise.reject(updated) : result.addedCount; });
    });
}

/**
 * Renames a plan (mask `name`). Resends the current description + selection so the write stays
 * correct even if it were ever treated as a full replace.
 */
export function renameRelayPlan(luid: string, name: string): Promise<void> {
  return readPlanForWrite(luid)
    .then(function(plan) {
      return WebHttpClient.sendRequest(new UpdateContentRelayPlanRequest({
        luid: luid,
        name: name,
        description: plan.description,
        selectionQueries: plan.selectionQueries || [],
        updateMask: ['name'],
      })).then(function(updated: any) { return hasErrors(updated) ? Promise.reject(updated) : undefined; });
    });
}

/**
 * Sets a plan's description. Backs the shared "Edit description" action (SetDescriptionSaga) so plans
 * reuse the same description editor as workbooks/data sources instead of a bespoke dialog. Mask
 * `description`; resends the current name + selection like the other writers. Resolves to an IResult
 * for the saga's error check.
 */
export function setRelayPlanDescription(luid: string, description: string): Promise<IResult> {
  return readPlanForWrite(luid)
    .then(function(plan) {
      return WebHttpClient.sendRequest(new UpdateContentRelayPlanRequest({
        luid: luid,
        name: plan.name,
        description: description,
        selectionQueries: plan.selectionQueries || [],
        updateMask: ['description'],
      }));
    })
    .then(function(result: any): IResult {
      // updatePlan resolves the plan detail (no `errors` field in its typed shape). Forward any
      // HTTP-200-with-errors payload (e.g. code 17 when the gRPC service is unreachable) as an
      // IResult so the shared description dialog reports failure instead of a false success.
      return result && result.errors ? { errors: result.errors } : {};
    });
}

/** Deletes one or more plans by LUID (one deletePlan request each). */
export function deleteRelayPlans(luids: ReadonlyArray<string>): Promise<void> {
  return Promise.all(luids.map(function(luid) {
    return WebHttpClient.sendRequest(new DeleteContentRelayPlanRequest({ luid: luid }));
  })).then(function() { return undefined; });
}

/**
 * Replaces a plan's membership with the given selection (e.g. after removing items on the detail
 * page). Like the other writers, resends the current name/description so a full-replace write is safe.
 */
export function setRelayPlanSelection(luid: string, buildSelection: (current: ISelectionQuery[]) => ISelectionQuery[]): Promise<IContentRelayPlanDetail> {
  return readPlanForWrite(luid)
    .then(function(plan) {
      return WebHttpClient.sendRequest(new UpdateContentRelayPlanRequest({
        luid: luid,
        name: plan.name,
        description: plan.description,
        selectionQueries: buildSelection(plan.selectionQueries || []),
        updateMask: SELECTION_QUERIES_MASK,
      }));
    });
}
